import { useCustomersListQuery } from "@/services/backend/customers/customer/customer-service";
import {
  type CustomerInspectionSummary,
  CustomerStatisticsRequest,
  useGetCustomerStatisticsQuery,
} from "@/services/backend/inspections/stats-service";
import { RTKQueryErrorAlert } from "@/shared/components/domain/errors/rtk-query-error-alert";
import {
  DataTable,
  DataTableContainer,
  useDataTableControllerWithSorting,
} from "@/shared/components/data-table";
import { ServiceObjectName } from "@/shared/components/domain/customer/service-object-name";
import { InspectionReportNumber } from "@/shared/components/domain/inspection/inspection-report-number";
import { ProcessNumber } from "@/shared/components/domain/processes/process-number";
import { DatePicker } from "@/shared/components/ui/date-picker";
import { Label } from "@/shared/components/ui/label";
import { PaginationLimitOffset } from "@/shared/components/ui/pagination-limit-offset";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { formatDate } from "@/shared/lib/utilities/date";
import type { ColumnDef } from "@tanstack/react-table";
import { de } from "date-fns/locale";
import { useState } from "react";
import { CustomersTotalRow } from "./customer-totals-row";

function firstDayOfLastMonth(): Date {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  d.setHours(0, 0, 0, 0);
  return d;
}

function lastDayOfLastMonth(): Date {
  const d = new Date();
  d.setDate(0);
  d.setHours(23, 59, 59, 999);
  return d;
}

const tableColumns: ColumnDef<CustomerInspectionSummary>[] = [
  {
    id: "processId",
    header: "Vorgang",
    cell: ({ row }) => <ProcessNumber processId={row.original.processId} />,
  },
  {
    id: "inspectionId",
    header: "Inspektion",
    cell: ({ row }) => (
      <InspectionReportNumber artefactId={row.original.inspectionId} />
    ),
  },
  {
    id: "serviceObjectId",
    header: "Service-Objekt",
    cell: ({ row }) => (
      <ServiceObjectName serviceObjectId={row.original.serviceObjectId} />
    ),
  },
  {
    id: "releaseDate",
    header: "Freigabedatum",
    cell: ({ row }) => <span>{formatDate(row.original.releaseDate, de)}</span>,
  },
  {
    id: "surfaceMicrobialCount",
    accessorKey: "surfaceMicrobialCount",
    header: "AK",
    cell: ({ row }) => row.original.surfaceMicrobialCount,
  },
  {
    id: "airMicrobialCount",
    accessorKey: "airMicrobialCount",
    header: "LK",
    cell: ({ row }) => row.original.airMicrobialCount,
  },
  {
    id: "differentiationCount",
    accessorKey: "differentiationCount",
    header: "Differenzierung",
    cell: ({ row }) => row.original.differentiationCount,
  },
];

export function CustomerStatsTable() {
  const [request, setRequest] = useState<CustomerStatisticsRequest>({
    customerId: "",
    from: firstDayOfLastMonth(),
    to: lastDayOfLastMonth(),
    limit: 10,
    offset: 0,
  });
  const { data: customersList } = useCustomersListQuery(
    { limit: 1000, offset: 0 },
    { refetchOnMountOrArgChange: true },
  );

  const {
    data: list,
    isLoading,
    isFetching,
    error,
  } = useGetCustomerStatisticsQuery(request, {
    skip: request.customerId === "",
    refetchOnMountOrArgChange: true,
  });

  const { table } = useDataTableControllerWithSorting(
    tableColumns,
    list?.items || [],
  );

  if (error) return <RTKQueryErrorAlert error={error} />;

  return (
    <div className="flex w-full flex-col space-y-4">
      <div className="flex items-end gap-4">
        <div className="flex flex-col gap-1.5">
          <Label>Kunde</Label>
          <Select
            value={request.customerId}
            onValueChange={(value) =>
              setRequest({ ...request, customerId: value })
            }
          >
            <SelectTrigger className="w-56">
              <SelectValue placeholder="Kunden auswählen" />
            </SelectTrigger>
            <SelectContent>
              {customersList?.items.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>Von</Label>
          <DatePicker
            date={request.from}
            onDateChange={(val) =>
              setRequest({ ...request, from: val as Date })
            }
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>Bis</Label>
          <DatePicker
            date={request.to}
            onDateChange={(val) => setRequest({ ...request, to: val as Date })}
          />
        </div>
      </div>

      <CustomersTotalRow
        customerId={request.customerId}
        from={request.from}
        to={request.to}
      />

      <DataTableContainer className="relative">
        <DataTable table={table} columns={tableColumns} />
        {(isFetching || isLoading) && (
          <div className="bg-background/60 pointer-events-none absolute inset-0 flex items-center justify-center backdrop-blur-sm">
            <span className="text-sm opacity-70">Laden…</span>
          </div>
        )}
      </DataTableContainer>

      {list != null && list.count > 0 && (
        <div className="flex justify-end">
          <PaginationLimitOffset
            offset={request.offset}
            limit={request.limit}
            totalCount={list.count}
            setLimitOffset={(limit, offset) =>
              setRequest({ ...request, limit, offset })
            }
          />
        </div>
      )}
    </div>
  );
}
